/*
 * Adaptive bitrate ladder for the published camera.
 *
 * Before this, produce() was called with nothing but a track: one browser-chosen encoding
 * off a 1080p capture, ramping up from libwebrtc's default start bitrate and adapting only
 * within that single stream. Three things were missing — a defined set of quality steps, a
 * sensible place to start, and any way for the server to give a student on 3G something
 * different from a student on fibre.
 *
 * The rungs are 360p, 480p and 720p, produced by scaling the camera down rather than
 * capturing smaller: the local recording reads the raw camera stream, so dropping the
 * capture would quietly downgrade every recording made from then on.
 *
 * The scaling factors are computed against the capture that actually arrived, never the one
 * that was asked for. getUserMedia treats {ideal: 1080} as a preference, and a laptop webcam
 * that tops out at 720p simply hands back something smaller. Divisors written for 1080p then
 * produce a 240/320/480 ladder while every label still claims 360/480/720.
 *
 * Bitrates are the well-worn figures for 30fps VP8 at each size. They are ceilings, not
 * targets — the encoder spends less on a static talking head and only approaches these on
 * motion.
 */

// Ordered low to high, which is the order mediasoup-client expects, and the order
// setMaxSpatialLayer indexes into.
export const VIDEO_LADDER = [
    {rid: 'r0', label: '360p', height: 360, maxBitrate: 400000},
    {rid: 'r1', label: '480p', height: 480, maxBitrate: 900000},
    {rid: 'r2', label: '720p', height: 720, maxBitrate: 2500000},
];

export const TOP_RUNG = VIDEO_LADDER.length - 1;

/*
 * Publish ONE encoding at a fixed size. The simulcast ladder below is built but not sent.
 *
 * This is on because the ladder was a regression, and the report that made that clear was
 * the plainest one available: audio was fine before the new broadcast implementation and
 * broke after it. What that implementation added on the publish path was exactly this —
 * before it, produce() was called with `{track}` and nothing else, so the client sent one
 * encoding at one resolution for the whole class.
 *
 * Why one constant resolution matters more here than adaptation does: nothing consumes this
 * stream as WebRTC. It is ingested by an RTMP bridge and transcoded by three ffmpeg
 * processes. When the ladder changes rung, the spatial layer the server forwards changes,
 * and all three see their input resolution change at the same instant. With reinit_filter on
 * they rebuild their scale graphs — video stalls for that moment while audio keeps arriving,
 * the muxing queues fall out of balance, and the audio that comes out carries a discontinuous
 * presentation timeline. The samples are fine; the timeline is not. Chrome's MSE refuses to
 * paper over that and cracks audibly, while ffmpeg- and VLC-based players absorb it in
 * silence — which is the entire shape of the report, down to the same stream sounding clean
 * in an external player and fine on mobile.
 *
 * And the adaptation was never ours to do. The backend already publishes 360/480/720 as
 * separate renditions; a student on 3G picks a smaller one from the HLS ladder. Running a
 * second ladder at the ingest gave viewers nothing they did not already have, and cost the
 * transcoder a stable input.
 *
 * The ladder is deliberately left in place rather than deleted. If this pipeline ever feeds
 * WebRTC consumers directly — no RTMP, no ffmpeg — simulcast becomes the right answer again,
 * and this is one line.
 */
export const LOCK_SINGLE_ENCODING = true;
// Which rung that single encoding sits at. Changed to 0 (360p, 400kbps) to prevent
// network buffering on constrained uplinks. 480p (900kbps) was causing the WebRTC 
// send queue to back up when maintain-resolution was enforced.
export const LOCKED_RUNG = 0;

/**
 * Encodings for a camera that actually delivered `captureHeight` pixels.
 *
 * L1T3 gives each spatial layer three temporal ones, so the server can thin a stream to
 * 15fps or 7.5fps before dropping it a size. Adaptation gets finer steps for free — the
 * temporal layers are already in the bitstream.
 *
 * Rungs taller than the capture are dropped, not clamped: scaleResolutionDownBy below 1 is
 * invalid, and upscaling 480p into a layer labelled 720p would spend 720p's bitrate to carry
 * no more detail than 480p had.
 */
export const encodingsForCapture = (captureHeight) => {
    const source = Number.isFinite(captureHeight) && captureHeight > 0 ? captureHeight : 1080;
    if (LOCK_SINGLE_ENCODING){
        // No rid: one encoding stands for the top, which is what _rescaleEncodings already
        // assumes of a single-entry list, and what the simulcast fallback in produce() sends.
        const rung = VIDEO_LADDER[Math.min(LOCKED_RUNG, TOP_RUNG)];
        const target = Math.min(rung.height, source);
        return [{
            maxBitrate: rung.maxBitrate,
            scaleResolutionDownBy: Math.max(source / target, 1),
            scalabilityMode: 'L1T3',
        }];
    }
    // 2% of slack, so a 718-line capture still gets its 720p rung.
    const usable = VIDEO_LADDER.filter((rung) => rung.height <= source * 1.02);
    return (usable.length ? usable : [VIDEO_LADDER[0]]).map((rung) => ({
        rid: rung.rid,
        maxBitrate: rung.maxBitrate,
        scaleResolutionDownBy: Math.max(source / rung.height, 1),
        scalabilityMode: 'L1T3',
    }));
};

// The one resolution nothing may exceed. The ladder tops out here, and so does screen share
// — which is what a bare encoding does not do on its own: with no scaleResolutionDownBy it
// publishes the monitor at its native size, so a 1080p or 1440p display was going out whole
// and reporting itself as a rung that does not exist.
export const MAX_HEIGHT = 720;

/**
 * Screen share: one encoding, not three, and capped at MAX_HEIGHT.
 *
 * The point of a 360p rung is that a small moving picture still reads; shared slides or code
 * at 360p do not read at all, so a student handed that layer is worse off than one waiting.
 * Screen therefore keeps one stream and gives up frame rate rather than size — the trade
 * maintain-resolution makes.
 *
 * The ceiling was 2 Mbps, which is a figure for 720p30 of moving video. A shared screen is
 * not that: slides and code are near-static between changes, so the encoder rarely asks for
 * anything like it — but 'maintain-resolution' means that when it does ask, it will not back
 * down, and on a home uplink that is the moment the audio sharing the transport starts
 * queueing behind it. 1.5 Mbps is still comfortably above what a page of text at 720p costs
 * and leaves headroom on the lines people actually teach from. Lower this further before
 * touching resolution if screen shares still strain: legibility is the whole point of the
 * mode, and frame rate is what it is supposed to spend.
 */
export const SCREEN_MAX_BITRATE = 1500000;

export const screenEncodingsForCapture = (captureHeight) => {
    const source = Number.isFinite(captureHeight) && captureHeight > 0 ? captureHeight : MAX_HEIGHT;
    return [{
        maxBitrate: SCREEN_MAX_BITRATE,
        scaleResolutionDownBy: Math.max(source / MAX_HEIGHT, 1),
        scalabilityMode: 'L1T3',
    }];
};

/**
 * What a single unnamed encoding is for: the size it should carry and what it may spend.
 *
 * `encodingsForCapture` and `screenEncodingsForCapture` both build one ridless encoding, and
 * once built the two are indistinguishable — which matters because the Share and Whiteboard
 * buttons do not build one. They `replaceTrack` on the producer the camera already created,
 * so whatever encoding the camera got is the encoding a shared screen then goes out on.
 *
 * That was survivable while a single encoding only ever meant "screen". With
 * LOCK_SINGLE_ENCODING the camera has one too, and the two want opposite things: the camera
 * is held at LOCKED_RUNG to keep the uplink clear, a screen is held at 720p because
 * legibility is the entire point of the mode. Asking here, from the one fact that separates
 * them, is what stops a swap inheriting the other one's budget.
 */
export const singleEncodingTarget = (screen) => (screen
    ? {height: MAX_HEIGHT, maxBitrate: SCREEN_MAX_BITRATE}
    : {
        height: VIDEO_LADDER[Math.min(LOCKED_RUNG, TOP_RUNG)].height,
        maxBitrate: VIDEO_LADDER[Math.min(LOCKED_RUNG, TOP_RUNG)].maxBitrate,
    });

/*
 * ---- adapting when the picture is not allowed to move -----------------------------------
 *
 * LOCK_SINGLE_ENCODING took the ladder away, and 'maintain-resolution' took the encoder's
 * own fallback away with it. What that leaves on a tightening uplink is one fixed size the
 * encoder can only pay for by shedding frames — whenever it likes, by however much it likes,
 * because nothing above it is choosing on its behalf any more. A talking head that falls to
 * six frames a second and climbs back is not something the RTMP bridge can hand ffmpeg
 * cleanly: the input cadence wanders, segment durations stop matching the length they
 * declare, and a player that has budgeted for the old rate runs dry. That is what reaches a
 * student as buffering, and it starts on the publisher's contended Wi-Fi rather than theirs.
 *
 * So the budget adapts instead of the geometry. Each step is a bitrate paired with the frame
 * rate that bitrate can honestly carry at this size, and moving between steps changes
 * neither scaleResolutionDownBy nor anything else ffmpeg can read as a new input format — no
 * scale-graph rebuild, none of the timeline damage that made the simulcast ladder a
 * regression here. What a viewer notices is motion getting cheaper, which is the one thing a
 * lecture can afford to give up.
 *
 * Step 0 is exactly what was being sent before any of this existed, frame-rate cap included
 * (there is none), so a class on a healthy line behaves identically and only a class already
 * in trouble ever leaves it.
 *
 * The floor is 120kbps at 15fps. Below that 360p stops being a face and becomes a smear, and
 * a class is better served by the "turn your camera off" prompt — which reads the same
 * samples — than by a picture nobody can make anything out in.
 */
export const BITRATE_STEPS = [
    {maxBitrate: 400000, maxFramerate: null},
    {maxBitrate: 280000, maxFramerate: 24},
    {maxBitrate: 190000, maxFramerate: 20},
    {maxBitrate: 120000, maxFramerate: 15},
];

/*
 * The same idea for a shared screen, weighted the opposite way.
 *
 * Slides and code are legible at 5fps and illegible at 360p, so these steps spend frame rate
 * freely and never touch the 720p. That is the trade 'maintain-resolution' already makes for
 * a screen; saying it in steps only takes the timing of it away from the encoder, which
 * otherwise improvises at whatever moment its estimate happens to move.
 */
export const SCREEN_BITRATE_STEPS = [
    {maxBitrate: SCREEN_MAX_BITRATE, maxFramerate: null},
    {maxBitrate: 1000000, maxFramerate: 12},
    {maxBitrate: 700000, maxFramerate: 8},
    {maxBitrate: 450000, maxFramerate: 5},
];

// Which table a producer is on follows the same one fact everything else here splits on:
// whether what it is carrying is detail-first content. See singleEncodingTarget.
export const bitrateStepsFor = (screen) => (screen ? SCREEN_BITRATE_STEPS : BITRATE_STEPS);

/*
 * Down on two samples, back up on six — four seconds to retreat, twelve to return.
 *
 * The asymmetry is the ladder's, for the ladder's reason: being wrong is not equally cheap
 * in both directions. Dropping to 280kbps during a lull that passes costs a few seconds of
 * cheaper motion. Climbing back onto a line that has not really recovered costs the class
 * another round of congestion, and then the trip down again.
 */
export const BUDGET_DOWN_VOTES = 2;
export const BUDGET_UP_VOTES = 6;

// Never aim the video at the whole line. Audio, retransmits, RTCP, the student's other tabs
// and — on a shared home connection — everyone else in the house all come out of the same
// pipe, and an encoder told it may have 100% of a measured 2 Mbps will find the ceiling by
// hitting it. Just over half leaves room to be wrong.
export const USABLE_SHARE = 0.55;

/**
 * Highest rung a measured line can carry, as an index into VIDEO_LADDER.
 *
 * Passing null (no measurement yet) deliberately does not return the top. A class that
 * starts at 720p on a line that turns out to be 3G spends its first ten seconds freezing
 * while congestion control works that out, and the teacher's opening sentence is the worst
 * part of a lesson to lose. The middle rung climbs within seconds if the line is good.
 */
export const rungForSpeed = (bps) => {
    if (!Number.isFinite(bps) || bps <= 0) return 1
    const budget = bps * USABLE_SHARE;
    for (let index = TOP_RUNG; index > 0; index--) {
        if (budget >= VIDEO_LADDER[index].maxBitrate) return index
    }
    return 0
};

/*
 * ---- weak line, and earning the way back off it -----------------------------------------
 *
 * The threshold is not a round number picked by hand; it is the point where the second rung
 * stops being affordable. 480p costs 900kbps and only USABLE_SHARE of the line may go to
 * video, so below ~1.64 Mbps there is nothing above 360p worth attempting, and a connection
 * there should be held at the bottom rather than discovering it one broken rung at a time.
 */
export const WEAK_LINE_BPS = VIDEO_LADDER[1].maxBitrate / USABLE_SHARE;   // ~1.64 Mbps

/**
 * Is this measured line too weak for anything above the bottom rung?
 *
 * null — nothing measured yet — is deliberately not weak. A class whose first speed test has
 * not landed should start where rungForSpeed puts it, not be locked to 360p by a measurement
 * that does not exist.
 */
export const isWeakLine = (bps) => Number.isFinite(bps) && bps > 0 && bps < WEAK_LINE_BPS;

/*
 * Coming back up has to cost more than going down, or a line sitting on the threshold flaps
 * between 360p and 480p every few seconds — which looks far worse to a class than staying
 * at 360p would. Two things are asked for, and "stable" means both:
 *
 *   a measurement a quarter clear of the threshold, not merely back across it, and
 *   ten consecutive clean samples — the stats tick every 2s, so ~20 seconds of the
 *   connection behaving itself.
 *
 * Twenty seconds is the only honest reading of "until the network is stable" a client can
 * actually make: it cannot know the future, so it waits long enough that a recovery which
 * was really a lull has time to fall over again.
 */
export const RECOVERY_MARGIN = 1.25;
export const RECOVERY_SAMPLES = 10;

/*
 * When a frame is small enough to count as "this rung is not being carried".
 *
 * The camera runs maintain-framerate, which is the right trade for a talking head: when the
 * line tightens libwebrtc keeps the motion and shrinks the picture instead. That is where
 * 540 and 240 come from — sizes no rung asked for, that appear nowhere in the menu and
 * cannot be chosen. They are the encoder quietly doing its own adaptation underneath the
 * ladder's.
 *
 * Reading a sustained shortfall as strain hands that decision back to the ladder: it steps
 * down a rung, and what goes out is a size the ladder actually has.
 *
 * 0.85 rather than something tighter because an odd aspect ratio, or a scaleResolutionDownBy
 * that does not divide evenly, lands a few lines short of nominal with nothing wrong.
 */
export const DOWNSCALE_TOLERANCE = 0.85;

// How long after a rung change to disregard that signal. Three samples: an encoder brings a
// newly activated layer up to size over a second or two, and the ladder must not read its
// own ramp-up as the line failing and step straight back down.
export const RUNG_SETTLE_MS = 6000;

/*
 * ---- when to suggest turning the camera off ---------------------------------------------
 *
 * Half a minute of trouble: long enough that a lift, a Wi-Fi roam or one lost second never
 * raises it, short enough to arrive while the lesson can still be saved. Once answered —
 * either way — it stays away for five minutes, because a prompt that returns every thirty
 * seconds is one people learn to dismiss without reading.
 */
export const WEAK_PROMPT_MS = 30000;
export const WEAK_PROMPT_COOLDOWN_MS = 300000;

/*
 * And it is a proportion of that half minute, not an unbroken run of it.
 *
 * A connection that fails outright is the easy case. The one that actually ruins a class
 * fluctuates — four bad seconds, one good one, four more bad — and a rule waiting for thirty
 * *continuous* seconds of trouble is reset by every one of those good samples and so never
 * fires at all, precisely when it is most wanted. Three samples in five is bad enough to be
 * worth a word.
 */
export const WEAK_PROMPT_RATIO = 0.6;
// How long the notice itself stays on screen. It is a snackbar rather than a dialog, so it
// has to take itself away; long enough to read a sentence and decide, and safe to expire
// because WEAK_PROMPT_COOLDOWN_MS keeps the next one five minutes off.
export const WEAK_NOTICE_MS = 12000;
// Below this the window is too short to draw a proportion from: a class eight seconds into
// publishing has not yet earned an opinion about its connection.
export const WEAK_PROMPT_MIN_SAMPLES = 10;

/*
 * ---- the machine, not the line ------------------------------------------------------
 *
 * When libwebrtc reports 'cpu' rather than 'bandwidth', the encoder is the bottleneck and
 * the connection has nothing to do with it. Stepping the ladder down would be the wrong
 * remedy twice over: it spends the class's legibility on a problem that was never about
 * bandwidth, and it leaves the machine encoding the same number of frames.
 *
 * So the answer is frames, not pixels. A talking head at 15fps is entirely watchable; a
 * page of code at 360p is not readable at any frame rate. maintain-resolution and
 * maintain-framerate both stay as they were — this caps the rate the encoder is asked for
 * in the first place, rather than leaving libwebrtc to choose what to give up.
 *
 * Two samples before easing and four before restoring, for the same reason the ladder
 * climbs slower than it falls: a machine that has just stopped struggling usually has not.
 */
export const CPU_EASED_FPS = 15;
export const CPU_EASE_VOTES = 2;
export const CPU_RESTORE_VOTES = 4;

/**
 * What to hand mediasoup as codecOptions.
 *
 * videoGoogleStartBitrate is the "start with" half of adaptive bitrate and the part that is
 * usually left out: without it libwebrtc opens at roughly 300kbps and probes upward, so a
 * fast line spends its first several seconds looking like a slow one. Seeded from the
 * measured speed, the first frames go out at the rate the connection has already been shown
 * to carry.
 *
 * Values are kbps here, unlike maxBitrate on the encodings, which is bps.
 */
export const codecOptionsForRung = (rungIndex) => {
    const rung = VIDEO_LADDER[Math.max(0, Math.min(rungIndex, TOP_RUNG))];
    return {
        videoGoogleStartBitrate: Math.round(rung.maxBitrate / 1000),
        videoGoogleMinBitrate: Math.round(VIDEO_LADDER[0].maxBitrate / 2000),
        videoGoogleMaxBitrate: Math.round(VIDEO_LADDER[TOP_RUNG].maxBitrate / 1000),
    };
};

/**
 * Name a measured frame height, as one of the rungs the ladder offers.
 *
 * Encoders do not land on the nominal size. An odd aspect ratio or a scaleResolutionDownBy
 * that does not divide evenly gives 362 or 718; more often, libwebrtc's own answer to a
 * squeezed line is to shrink the picture, so a 720p rung goes out at 540 and a 360p rung at
 * 240. Reporting those verbatim put sizes in the quality button that exist nowhere in the
 * menu and cannot be picked — a readout answering a question nobody asked.
 *
 * So a measurement is named by the rung nearest it, and the exact pixel count stays one
 * hover away in describeFrame. The rung is the thing under control; a shortfall against it
 * is the controller's business, not the label's — see DOWNSCALE_TOLERANCE, which is what
 * stops the two disagreeing for long.
 */
export const labelForHeight = (height) => {
    if (!Number.isFinite(height) || height <= 0) return ''
    const nearest = VIDEO_LADDER.reduce((best, rung) => (
        Math.abs(height - rung.height) < Math.abs(height - best.height) ? rung : best
    ), VIDEO_LADDER[0]);
    return nearest.label
};

/** "1280×720 @ 30fps", or "" when there is nothing being measured yet. */
export const describeFrame = (frame) => {
    if (!frame?.width || !frame?.height) return ''
    const fps = Number.isFinite(frame.fps) ? ` @ ${Math.round(frame.fps)}fps` : '';
    return `${frame.width}×${frame.height}${fps}`
};
